/** The dot's fill in each colour scheme the browser chrome may use. */
interface BadgeColor {
  light: string
  dark: string
}

/** The dot drawn over the tab's icon. */
export interface FaviconBadge {
  color: BadgeColor
  /** True to breathe the dot over {@link FAVICON_PULSE_FRAMES} frames, false for one still frame. */
  pulse: boolean
}

/** How many frames one breath of a pulsing badge takes. */
export const FAVICON_PULSE_FRAMES = 12

const MARK_SIZE = 129
const DOT_CENTER = 100
const DOT_RADIUS = { min: 18, max: 26 }
const DOT_OPACITY = { min: 0.55, max: 1 }
const CUT_RADIUS = 34

/** The frames of the tab icon: the brand mark with a dot cut into its lower right corner, as SVG data URIs in play order. */
export function faviconFrames(mark: string, badge: FaviconBadge): string[] {
  const phases = badge.pulse ? pulsePhases(FAVICON_PULSE_FRAMES) : [1]
  return phases.map((phase) => toDataUri(frameSvg(mark, badge.color, phase)))
}

/** One breath as phases from 0 (smallest) to 1 (fullest), starting full. */
function pulsePhases(count: number): number[] {
  return Array.from(
    { length: count },
    (_, index) => (1 + Math.cos((2 * Math.PI * index) / count)) / 2,
  )
}

function between(range: { min: number; max: number }, phase: number): number {
  return range.min + (range.max - range.min) * phase
}

function round(value: number): string {
  return value.toFixed(2)
}

function frameSvg(mark: string, color: BadgeColor, phase: number): string {
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${MARK_SIZE} ${MARK_SIZE}">`,
    `<style>.badge{fill:${color.light}}@media (prefers-color-scheme: dark){.badge{fill:${color.dark}}}</style>`,
    `<mask id="badge-cut"><rect width="${MARK_SIZE}" height="${MARK_SIZE}" fill="white"/><circle cx="${DOT_CENTER}" cy="${DOT_CENTER}" r="${CUT_RADIUS}" fill="black"/></mask>`,
    `<g mask="url(#badge-cut)">${mark}</g>`,
    `<circle class="badge" cx="${DOT_CENTER}" cy="${DOT_CENTER}" r="${round(between(DOT_RADIUS, phase))}" fill-opacity="${round(between(DOT_OPACITY, phase))}"/>`,
    '</svg>',
  ].join('')
}

function toDataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}
